/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { When } from "react-if";
import { BrickLoader, LoadMore } from "@nordcloud/gnui";
import { Resource, Group } from "~/generated/graphql";
import { useDuplicatedResources } from "~/hooks/useDuplicatedResources";
import { usePaginatedActionResources } from "~/views/resourceGroups/hooks/";
import { ResourceList } from "./ResourceList";

type Props = {
  resourceGroups: Group[];
  resources: Resource[];
};

export function PlanResourcesSection({ resourceGroups, resources }: Props) {
  const {
    loading,
    groupResources,
    hasPagination,
    visibleResourceGroups,
    visibleResources,
    loadMoreProps,
  } = usePaginatedActionResources(resourceGroups, resources);

  const duplicateState = useDuplicatedResources({
    resourceCollections: [groupResources, resources],
    actionChangeKey: useMemo(
      () => resourceGroups.map((group) => group.id).join(","),
      [resourceGroups]
    ),
  });

  if (loading) {
    return <BrickLoader height="14rem" />;
  }

  return (
    <>
      <ResourceList
        resourceGroups={visibleResourceGroups}
        resources={visibleResources}
        duplicateState={duplicateState}
      />
      <When condition={hasPagination}>
        <LoadMore {...loadMoreProps} />
      </When>
    </>
  );
}
